// lastScreen.js — 계정·기기마다 '마지막으로 본 왼쪽 메뉴 화면' (2026-10-26 사용자 결정 b·나 — 일반 직원까지 모두)
//   로그인 직후·사이트 주소만 쳐서 들어올 때 그 화면으로 연다. 없으면 종전 기본 화면.
//   기억 = 왼쪽 메뉴에 실제로 보인(지금 보는) 줄의 주소만(MenuItem) → 권한 없는 화면·수정 창·상세 화면은 기억하지 않는다.
//   이 기기(브라우저)에만 저장 — 휴대폰·PC 가 따로 기억한다. 위장 접속 탭은 기억도 복원도 하지 않는다.
//   복원했는데 그 주소가 왼쪽 메뉴에 안 보이면(그사이 열쇠가 빠짐) 기억을 지우고 기본 화면으로 — AdminLayout 문지기.
//   2026-10-29: 통합 메뉴가 아닌 '마지막으로 본 회사 화면'도 따로 기억한다 — 통합관리에서 회사를 고를 때 돌아갈 곳
//   (integratedScope exitIntegratedTarget). 통합관리에서 나와 첫 주소('/')로 갈 때는 통합 메뉴를 되살리지 않는다
//   (안 막으면 방금 보던 통합 메뉴를 되살려 통합관리로 되돌아갔다 — 파인그룹 김예현·표정).
import { apiClient } from '../api/apiClient';
import { isIntegratedPath } from '../pages/admin/adminlayout/integratedScope';

const OWNER_KEY = 'lastScreen:owner';            // 이 기기에서 마지막으로 로그인한 계정
const RESTORE_KEY = 'lastScreen:restoring';      // (sessionStorage) 이번에 복원으로 연 주소 — 메뉴에 보이면 지운다
const SKIP_INTEGRATED_KEY = 'lastScreen:skipIntegrated'; // (sessionStorage) 다음 한 번은 통합 메뉴를 되살리지 않는다
const keyOf = (loginId) => `lastScreen:${String(loginId || '').trim().toLowerCase()}`;
const companyKeyOf = (loginId) => `lastCompanyScreen:${String(loginId || '').trim().toLowerCase()}`;
const REMEMBERABLE = /^\/(admin|my)\/[^?#]*$/;

function safe(fn, fallback = null) {
  try { return fn(); } catch (_) { return fallback; }
}
const imp = () => safe(() => apiClient.isImpersonating(), false);

/** 로그인한 계정을 이 기기의 기억 주인으로 */
export function setLastScreenOwner(loginId) {
  if (!loginId || imp()) return;
  safe(() => localStorage.setItem(OWNER_KEY, String(loginId)));
}

/** 왼쪽 메뉴에서 지금 보는 줄의 주소를 기억(MenuItem 이 부른다) */
export function rememberLastScreen(path) {
  if (!path || imp() || !REMEMBERABLE.test(path)) return;
  const owner = safe(() => localStorage.getItem(OWNER_KEY));
  if (!owner) return;
  safe(() => localStorage.setItem(keyOf(owner), path));
  if (!isIntegratedPath(path)) safe(() => localStorage.setItem(companyKeyOf(owner), path));
  safe(() => { if (sessionStorage.getItem(RESTORE_KEY) === path) sessionStorage.removeItem(RESTORE_KEY); });
}

/** 마지막으로 본 회사 화면(통합 메뉴 아님) — 통합관리에서 회사를 고를 때 돌아갈 곳. 없으면 null */
export function lastCompanyScreen() {
  if (imp()) return null;
  const owner = safe(() => localStorage.getItem(OWNER_KEY));
  const path = owner ? safe(() => localStorage.getItem(companyKeyOf(owner))) : null;
  return path && REMEMBERABLE.test(path) && !isIntegratedPath(path) ? path : null;
}

/** 통합관리에서 나와 첫 주소로 갈 때 — 다음 한 번은 통합 메뉴를 되살리지 않는다 */
export function skipIntegratedRestoreOnce() {
  safe(() => sessionStorage.setItem(SKIP_INTEGRATED_KEY, '1'));
}

/** 복원할 주소(없으면 null) — 복원 표시를 남겨 AdminLayout 이 메뉴에 보이는지 확인하게 한다 */
export function takeLastScreen(loginId) {
  if (!loginId || imp()) return null;
  const path = safe(() => localStorage.getItem(keyOf(loginId)));
  const skipIntegrated = safe(() => sessionStorage.getItem(SKIP_INTEGRATED_KEY)) === '1';
  safe(() => sessionStorage.removeItem(SKIP_INTEGRATED_KEY));
  if (!path || !REMEMBERABLE.test(path)) return null;
  if (skipIntegrated && isIntegratedPath(path)) return null; // 방금 통합관리에서 나왔다 — 기본 화면으로
  // 이 탭에서 이미 한 번 이 화면으로 열었는데 왼쪽 메뉴에서 확인되지 않았다(그 화면 문지기가 첫 주소로 돌려보냄) →
  //   또 열면 '열기 → 돌려보냄'이 끝없이 돈다. 기억을 지우고 기본 화면으로(2026-10-26 시험에서 발견).
  if (safe(() => sessionStorage.getItem(RESTORE_KEY)) === path) {
    dropLastScreen();
    return null;
  }
  safe(() => sessionStorage.setItem(RESTORE_KEY, path));
  return path;
}

export function pendingRestore() {
  return safe(() => sessionStorage.getItem(RESTORE_KEY));
}

export function clearRestoreMark() {
  safe(() => sessionStorage.removeItem(RESTORE_KEY));
}

/** 복원한 주소가 메뉴에 없을 때 — 그 계정의 기억과 복원 표시를 지운다 */
export function dropLastScreen() {
  const owner = safe(() => localStorage.getItem(OWNER_KEY));
  safe(() => { if (owner) localStorage.removeItem(keyOf(owner)); });
  clearRestoreMark();
}
